/**
 * A quiet room behind her voice, for the browser lane.
 *
 * Nima's note after a call with a company's AI agent: a fan behind the voice
 * made it feel like a person in an office, and the dead pause before an AI
 * answers is what gives it away. So while a conversation is open this plays
 * a room you half notice (an empty gym for Buddy, a training space for every
 * other sport), a far ball bounce now and then, and, the instant the player
 * stops talking, one short sound that covers the wait until her first word:
 * a close dribble, a ball caught, a breath out, or a soft "um" in her voice.
 *
 * Same behaviour as the Pipecat mixer (voice-stacks/ambience/hb_ambience.py),
 * same files and levels (public/audio/ambience/manifest.json):
 *   - the bed loops for the whole conversation, from a random point;
 *   - one-shots at random log-normal gaps (median 24 s, 9 to 75 s), never the
 *     same one twice running, a little gain and pitch variation each time;
 *   - bed, one-shots and ball fillers dip 8 dB under her voice (150 ms in,
 *     400 ms back); no one-shot starts while the player talks, while she
 *     talks, or in the 5 s after her voice stops (when the player most likely
 *     starts): a far bounce on a quiet player's first word can change it;
 *   - one filler per turn, 120 to 280 ms after the player stops, skipped when
 *     she answers first or an acknowledgement is coming, never the same one
 *     twice running, and never again until she has spoken, so it cannot loop;
 *     a spoken "um" only when it is known no acknowledgement is coming, and it
 *     is cut in 60 ms the moment her real voice starts;
 *   - off is off (setLevel('off')), and it goes quiet while the tab is hidden.
 *
 * Nothing here listens: this only makes sound. The microphone is the voice
 * loop's, which sends silence to the ears whenever it is not the player's turn.
 *
 *   import { ambience } from './ambience.js';
 *   ambience.start('basketball');        // a companion id or a scene name
 *   ambience.onUserStarted();             // the ears hear the player start (StartOfTurn)
 *   ambience.onUserStopped({ acked });   // acked: true, false, or undefined if not known
 *   ambience.onHerSpeechStart(); ambience.onHerSpeechEnd();
 *   ambience.setVoice('aura-luna'); ambience.setLevel('off' | 'subtle'); ambience.stop();
 */

const MANIFEST = '/audio/ambience/manifest.json';
const PREF = 'hb-ambience';
const LEVELS = ['off', 'subtle'];
const FILLER_DELAY_MS = [120, 280];
const VOICE_FILLER_SHARE = 0.35;
const REFILL_AFTER_MS = 20000;
const REPLY_WINDOW_MS = 5000;
const CROSSFADE_S = 1.5;
const FADE_S = 0.3;

const readPref = () => { try { return localStorage.getItem(PREF); } catch { return null; } };
const writePref = (v) => { try { localStorage.setItem(PREF, v); } catch { /* private mode */ } };
const dbToGain = (db) => 10 ** (db / 20);
const rand = (a, b) => a + Math.random() * (b - a);
const gauss = () => { let u = 0, v = 0; while (!u) u = Math.random(); while (!v) v = Math.random(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };

export function createAmbience({ manifestUrl = MANIFEST } = {}) {
  let manifest = null, loading = null, ctx = null;
  let master = null, duck = null;
  const buffers = new Map();              // id -> AudioBuffer
  let level = LEVELS.includes(readPref()) ? readPref() : 'subtle';
  let running = false, scene = null, voice = null, wanted = null;
  let bed = null;                         // { src, gain }
  let oneshotTimer = null, lastOneshot = null;
  let fillerTimer = null, fillerThisTurn = false, lastFiller = null, lastFillerAt = -Infinity, herSpokeSinceFiller = true;
  let skipNext = false, verbalOk = false, userTalking = false, herTalking = false, herEndAt = -Infinity;
  const playing = new Set();              // { src, gain, role }
  let hiddenPaused = false;

  const trimDb = () => manifest?.levels?.playback_trim_db ?? -8;
  const duckDb = () => manifest?.levels?.duck_db ?? -8;
  const now = () => ctx?.currentTime ?? 0;

  async function load() {
    if (manifest) return manifest;
    loading ??= fetch(manifestUrl).then((r) => { if (!r.ok) throw new Error(`ambience ${r.status}`); return r.json(); })
      .then((m) => { manifest = m; return m; })
      .catch((e) => { loading = null; throw e; });
    return loading;
  }

  /** One sound, decoded once. Opus in WebM first; AAC for a browser that cannot decode it. */
  async function buffer(id) {
    if (buffers.has(id)) return buffers.get(id);
    const f = manifest.files.find((x) => x.id === id);
    if (!f) return null;
    const p = (async () => {
      for (const url of [f.webm, f.m4a]) {
        try {
          const r = await fetch(url);
          if (!r.ok) continue;
          return await ctx.decodeAudioData(await r.arrayBuffer());
        } catch { /* try the next format */ }
      }
      return null;
    })();
    buffers.set(id, p);
    const b = await p;
    if (!b) buffers.delete(id);
    return b;
  }

  function sceneFor(name) {
    const scenes = manifest.scenes;
    if (scenes[name]) return name;
    for (const [k, s] of Object.entries(scenes)) if ((s.companions || []).includes(name)) return k;
    for (const [k, s] of Object.entries(scenes)) if ((s.companions || []).includes('*')) return k;
    return Object.keys(scenes)[0];
  }

  function ensureGraph() {
    if (ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC({ latencyHint: 'playback' });
    master = ctx.createGain();
    master.gain.value = 0;
    duck = ctx.createGain();
    duck.connect(master);
    master.connect(ctx.destination);
  }

  const masterTarget = () => (level === 'off' || hiddenPaused ? 0 : dbToGain(trimDb()));
  function rampMaster() {
    if (!master) return;
    master.gain.cancelScheduledValues(now());
    master.gain.setTargetAtTime(masterTarget(), now(), FADE_S / 3);
  }

  async function startBed(name) {
    const b = manifest.files.find((f) => f.scene === name && f.role === 'bed');
    const buf = b && await buffer(b.id);
    if (!buf || !running || scene !== name) return;
    const src = ctx.createBufferSource();
    src.buffer = buf; src.loop = true;
    // Loop the bed's real length, not the decoded buffer's: an AAC decode can carry
    // up to a frame of encoder padding at the end (16 ms of near silence measured),
    // which would drop the room out for a moment on every wrap.
    const loopEnd = Math.min(buf.duration, b.seconds || buf.duration);
    src.loopStart = 0; src.loopEnd = loopEnd;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain).connect(duck);
    src.start(0, Math.random() * loopEnd);
    gain.gain.setTargetAtTime(1, now(), (bed ? CROSSFADE_S : 0.4) / 3);
    const old = bed;
    bed = { src, gain };
    if (old) {
      old.gain.gain.setTargetAtTime(0, now(), CROSSFADE_S / 3);
      try { old.src.stop(now() + CROSSFADE_S * 2); } catch {}
    }
  }

  function play(id, buf, { rate = 1, gainDb = 0, ducked = true, role }) {
    const src = ctx.createBufferSource();
    src.buffer = buf; src.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = dbToGain(gainDb);
    src.connect(gain).connect(ducked ? duck : master);
    const item = { id, src, gain, role };
    playing.add(item);
    src.onended = () => playing.delete(item);
    src.start();
    return item;
  }

  function fadeOut(item, ms) {
    try {
      item.gain.gain.cancelScheduledValues(now());
      item.gain.gain.setValueAtTime(item.gain.gain.value, now());
      item.gain.gain.linearRampToValueAtTime(0, now() + ms / 1000);
      item.src.stop(now() + ms / 1000 + 0.02);
    } catch {}
    item.fading = true;
  }

  function pick(ids, last) {
    const pool = ids.filter((id) => id !== last);
    const list = pool.length ? pool : ids;
    if (!list.length) return null;
    const weights = list.map((id) => Math.max(manifest.files.find((f) => f.id === id)?.weight ?? 1, 0.001));
    let r = Math.random() * weights.reduce((a, b) => a + b, 0);
    for (let i = 0; i < list.length; i++) { r -= weights[i]; if (r <= 0) return list[i]; }
    return list[list.length - 1];
  }

  // ---------------------------------------------------------------- one-shots
  function gapMs() {
    const g = manifest.oneshot_gap_seconds || { min: 9, median: 24, max: 75 };
    return Math.min(g.max, Math.max(g.min, Math.exp(Math.log(g.median) + 0.6 * gauss()))) * 1000;
  }
  function scheduleOneshot(ms) {
    clearTimeout(oneshotTimer);
    oneshotTimer = setTimeout(async () => {
      if (!running) return;
      const busy = userTalking || herTalking || performance.now() - herEndAt < REPLY_WINDOW_MS
        || [...playing].some((p) => p.role === 'filler' || p.role === 'voice-filler');
      if (busy || level === 'off' || hiddenPaused) { scheduleOneshot(busy ? 1500 : gapMs()); return; }
      const ids = manifest.files.filter((f) => f.scene === scene && f.role === 'oneshot').map((f) => f.id);
      const id = pick(ids, lastOneshot);
      const buf = id && await buffer(id);
      if (buf && running) { lastOneshot = id; play(id, buf, { rate: [0.93, 1, 1, 1.06][Math.floor(Math.random() * 4)], gainDb: rand(-3, 1.5), role: 'oneshot' }); }
      scheduleOneshot(gapMs());
    }, ms);
  }

  // ---------------------------------------------------------------- fillers
  function fadeFillers(verbalOnly, ms = 60) {
    for (const p of playing) if (!p.fading && (p.role === 'voice-filler' || (!verbalOnly && p.role === 'filler'))) fadeOut(p, ms);
  }
  async function playFiller() {
    fillerTimer = null;
    if (!running || level === 'off' || hiddenPaused) return;
    const nonverbal = manifest.scenes[scene]?.fillers || [];
    const verbal = (voice && manifest.voice_fillers?.[voice]) || [];
    const useVerbal = verbalOk && verbal.length && Math.random() < VOICE_FILLER_SHARE;
    const id = pick(useVerbal ? verbal : nonverbal, lastFiller);
    const buf = id && await buffer(id);
    if (!buf || !running || !fillerThisTurn) return;
    lastFiller = id; lastFillerAt = performance.now(); herSpokeSinceFiller = false;
    play(id, buf, { gainDb: rand(-1.5, 0.5), ducked: !useVerbal, role: useVerbal ? 'voice-filler' : 'filler' });
  }

  // ---------------------------------------------------------------- tab
  function onVisibility() {
    if (!ctx) return;
    hiddenPaused = document.visibilityState === 'hidden';
    rampMaster();
    if (hiddenPaused) {
      clearTimeout(fillerTimer); fillerTimer = null;
      // Suspend once the fade has settled (9 time constants, about -78 dB): at 400 ms a
      // busy device was measured suspending with the room only 14 dB down.
      setTimeout(() => { if (hiddenPaused && ctx?.state === 'running') ctx.suspend().catch(() => {}); }, FADE_S * 1000 * 3);
    } else if (running && level !== 'off') ctx.resume().catch(() => {});
  }

  return {
    /** Start (or switch to) a scene: a companion id ('basketball', 'soccer'...) or a scene name. Call from a user gesture. */
    async start(companionOrScene = 'basketball') {
      wanted = companionOrScene;
      if (level === 'off') return;
      try { await load(); } catch { return; }
      ensureGraph();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const next = sceneFor(companionOrScene);
      const first = !running;
      running = true;
      if (first) {
        document.addEventListener('visibilitychange', onVisibility);
        hiddenPaused = document.visibilityState === 'hidden';
        rampMaster();
        scheduleOneshot(rand(6000, 20000));
      }
      if (next !== scene) { scene = next; startBed(next); }
      // Warm this scene's fillers and her voice's, so the first one plays at once.
      for (const id of [...(manifest.scenes[scene]?.fillers || []), ...((voice && manifest.voice_fillers?.[voice]) || [])]) buffer(id);
    },

    /** Fade out and stop everything. The decoded sounds are kept for the next start. */
    stop() {
      wanted = null;
      if (!running) return;
      running = false;
      clearTimeout(oneshotTimer); clearTimeout(fillerTimer); fillerTimer = null;
      document.removeEventListener('visibilitychange', onVisibility);
      if (master) { master.gain.cancelScheduledValues(now()); master.gain.setTargetAtTime(0, now(), FADE_S / 3); }
      const old = bed; bed = null; scene = null;
      const items = [...playing];
      setTimeout(() => {
        try { old?.src.stop(); } catch {}
        for (const p of items) { try { p.src.stop(); } catch {} }
        if (!running && ctx?.state === 'running') ctx.suspend().catch(() => {});
      }, FADE_S * 1000 * 2);
    },

    /** The player finished a turn. acked: true (an acknowledgement is coming), false (none), undefined (not known). */
    onUserStopped({ acked } = {}) {
      userTalking = false;
      if (!running || level === 'off' || hiddenPaused) return;
      if (acked === true || skipNext) { skipNext = false; return; }
      if (fillerTimer || fillerThisTurn) return;
      if (!herSpokeSinceFiller && performance.now() - lastFillerAt < REFILL_AFTER_MS) return;
      verbalOk = acked === false;
      fillerThisTurn = true;
      fillerTimer = setTimeout(playFiller, rand(...FILLER_DELAY_MS));
    },

    /** Known after the stop whether she says an acknowledgement first. */
    onAck(acked) {
      if (acked) { clearTimeout(fillerTimer); fillerTimer = null; fadeFillers(false, 80); if (!fillerThisTurn) skipNext = true; }
      else if (fillerTimer) verbalOk = true;
    },

    /**
     * The player started talking: no filler yet, and no far bounce over their words (a bounce
     * on a quiet player's first word was measured to change it for the ears). A sounding
     * one-shot fades in 150 ms and none starts until they stop. The bed stays.
     */
    onUserStarted() {
      clearTimeout(fillerTimer); fillerTimer = null; fillerThisTurn = false; userTalking = true;
      fadeFillers(true);
      for (const p of playing) if (!p.fading && p.role === 'oneshot') fadeOut(p, 150);
    },

    /** Her voice started: no filler now, any "um" cut, the room dips under her. */
    onHerSpeechStart() {
      clearTimeout(fillerTimer); fillerTimer = null;
      fadeFillers(true, 60);
      herSpokeSinceFiller = true; fillerThisTurn = false; skipNext = false; herTalking = true;
      if (duck) { duck.gain.cancelScheduledValues(now()); duck.gain.setTargetAtTime(dbToGain(duckDb()), now(), 0.15 / 3); }
    },

    /** Her voice stopped: the room comes back up over 400 ms. */
    onHerSpeechEnd() {
      herTalking = false; herEndAt = performance.now();
      if (duck) { duck.gain.cancelScheduledValues(now()); duck.gain.setTargetAtTime(1, now(), 0.4 / 3); }
    },

    /** 'aura-luna' or 'luna'. Picks which "um" she uses. */
    setVoice(v) { voice = String(v || '').toLowerCase().replace(/^aura[-_]?/, '') || null; },

    /** 'off' or 'subtle'. Remembered on this device. */
    setLevel(next) {
      if (!LEVELS.includes(next)) return;
      level = next; writePref(next);
      if (next === 'off') { clearTimeout(fillerTimer); fillerTimer = null; }
      rampMaster();
      if (next !== 'off' && wanted && !running) this.start(wanted);
    },
    get level() { return level; },
    get running() { return running; },
    /** True while a filler is sounding: the voice loop should not reopen the mic over it. */
    get fillerPlaying() { return [...playing].some((p) => !p.fading && (p.role === 'filler' || p.role === 'voice-filler')); },
  };
}

export const ambience = createAmbience();
